import { ApiError } from './api';
import { SESSION_PROTOCOL_VERSION } from './session-core';

export type ReportReason = 'harassment' | 'sexual_content' | 'hate_or_abuse' | 'spam' | 'unsafe_behavior' | 'other';
export type SafetyContext = { sessionId: string } | { groupRoomId: string } | { sayItInteractionId: string };

export interface SafetyState {
  target: { userId: string; firstName: string };
  blocked: boolean;
  reported: boolean;
}

const request = async <T>(path: string, initData: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...options, cache: 'no-store',
    headers: {
      Accept: 'application/json', Authorization: `tma ${initData}`, 'Content-Type': 'application/json',
      'X-UTAGE-Protocol': String(SESSION_PROTOCOL_VERSION), ...options?.headers
    }
  });
  const body = await response.json().catch(() => ({})) as T & { error?: { code?: string; message?: string } };
  if (!response.ok) throw new ApiError(body.error?.message ?? "Couldn't save that yet.", body.error?.code);
  return body;
};

const contextParams = (context: SafetyContext): Record<string, string> => 'sessionId' in context
  ? { sessionId: context.sessionId } : 'groupRoomId' in context ? { groupRoomId: context.groupRoomId } : { sayItInteractionId: context.sayItInteractionId };

export const loadSafetyState = (initData: string, targetUserId: string, context: SafetyContext): Promise<SafetyState> => {
  const query = new URLSearchParams({ targetUserId, ...contextParams(context) });
  return request(`/safety/state?${query.toString()}`, initData);
};

const action = (targetUserId: string, context: SafetyContext, extra: Record<string, unknown> = {}): string => JSON.stringify({
  actionId: crypto.randomUUID(), targetUserId, ...contextParams(context), ...extra
});

export const blockUser = (initData: string, targetUserId: string, context: SafetyContext): Promise<SafetyState> =>
  request('/safety/block', initData, { method: 'POST', body: action(targetUserId, context) });

export const unblockUser = (initData: string, targetUserId: string, context: SafetyContext): Promise<SafetyState> =>
  request('/safety/unblock', initData, { method: 'POST', body: action(targetUserId, context) });

export const reportUser = (
  initData: string, targetUserId: string, context: SafetyContext, reason: ReportReason
): Promise<SafetyState> => request('/safety/report', initData, { method: 'POST', body: action(targetUserId, context, { reason }) });
